import { fetchEdgeFunction } from '../lib/api';

// Audio cache to avoid re-fetching same text/language pair
const audioCache = new Map();

function getCacheKey(text, languageCode) {
  if (!languageCode) return null;
  return `${languageCode}:${text}`;
}

/**
 * Play TTS audio for given text
 * @param {string} text - The text to convert to speech
 * @param {string} [languageCode] - Optional language code for cache isolation
 * @returns {Promise<void>}
 */
export async function playTTS(text, languageCode) {
  const cacheKey = getCacheKey(text, languageCode);

  // Check cache first
  if (cacheKey && audioCache.has(cacheKey)) {
    const audio = new Audio(audioCache.get(cacheKey));
    await audio.play();
    return;
  }

  // Call Edge Function (handles 401 globally)
  const response = await fetchEdgeFunction('mellow-ai-chat', {
    mode: 'tts',
    ttsText: text,
  });

  if (!response.ok) {
    throw new Error(`TTS failed (${response.status})`);
  }

  // Get audio blob and create URL
  const audioBlob = await response.blob();
  const audioUrl = URL.createObjectURL(audioBlob);

  // Cache for reuse
  if (cacheKey) {
    audioCache.set(cacheKey, audioUrl);
  }

  // Play audio
  const audio = new Audio(audioUrl);
  let cleanup = null;
  if (!cacheKey) {
    let cleaned = false;
    cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(audioUrl);
    };
    audio.addEventListener('ended', cleanup, { once: true });
    audio.addEventListener('error', cleanup, { once: true });
  }
  try {
    await audio.play();
  } catch (err) {
    cleanup?.();
    throw err;
  }
}

/**
 * Clear the TTS audio cache
 * Useful for memory management in long sessions
 */
export function clearTTSCache() {
  for (const audioUrl of audioCache.values()) {
    URL.revokeObjectURL(audioUrl);
  }
  audioCache.clear();
}
